import {Component} from "@angular/core";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {OrderService} from "./order.service";
import {RouteHelper, RouteMapping} from "../misc/route-helper";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {OrderCreateDialogComponent} from "./order-create-dialog.component";
import {OrderGqlModel} from "./order.gql-model";
import {handleErrors} from "../misc/handle-errors.decorator";
import {BreadcrumbGqlModel} from "../breadcrumbs/breadcrumb.gql-model";
import {ProjectService} from "../project/project.service";
import {SiteService} from "../sites/site.service";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  templateUrl: "./order-list.template.pug",
  providers: [
    OrderService,
    ProjectService,
    SiteService,
    DialogService,
  ],
})
export class OrderListComponent extends RouteHelper {
  orders: OrderGqlModel[] = [];
  breadcrumbs: BreadcrumbGqlModel[] = [];
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  totalCount: number = 0;
  searchText: string = "";
  error: Error;
  siteId: string;
  projectId: string;
  isMigrationActive: boolean = false;

  paramsMap: RouteMapping[] = [
    {fieldName: "currentPage", paramName: "page", defaultValue: DEFAULT_PAGE, type: "number"} as RouteMapping,
    {fieldName: "pageSize", paramName: "limit", defaultValue: DEFAULT_PAGE_LIMIT, type: "number"} as RouteMapping,
    {fieldName: "searchText", paramName: "search"} as RouteMapping,
    {fieldName: "siteId", paramName: "siteId"} as RouteMapping,
    {fieldName: "projectId", paramName: "projectId"} as RouteMapping,
  ];

  constructor(
    private orderService: OrderService,
    private projectService: ProjectService,
    private siteService: SiteService,
    public route: ActivatedRoute,
    public router: Router,
    public dialogService: DialogService,
  ) {
    super();
  }

  navigate(queryParams: Params, skipLocationChange: boolean = false): Promise<boolean> {
    return this.router.navigate(["orders"], {queryParams, skipLocationChange});
  }

  @handleErrors()
  async loadData() {
    const {rows, count, breadcrumbs} = await this.orderService.getOrders(
      `${+this.currentPage - 1}`,
      `${this.pageSize}`,
      this.searchText,
      this.projectId,
      this.siteId,
      true,
    );

    if (rows.length) {
      this.isMigrationActive = rows[0].isMigrationActive;
    } else if (this.projectId) {
      const project = await this.projectService.getProjectById(this.projectId).toPromise();
      this.isMigrationActive = project.isMigrationActive;
    } else {
      const site = await this.siteService.getSiteById(this.siteId);
      this.isMigrationActive = site.isMigrationActive;
    }

    this.orders = rows;
    this.maxPages = Math.ceil(count / (+this.pageSize));
    this.totalCount = count;
    this.breadcrumbs = breadcrumbs;
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }

    this.syncRoute();
  }

  @handleErrors()
  async create() {
    const result = await OrderCreateDialogComponent.show(this.dialogService, this.projectId, this.siteId);

    if (result) {
      await this.loadData();
    }
  }
}
